"use client";

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { CallAnalysisOverviewType } from "@/app/types/call/CallAnalysisOverview";
import { UStyles } from "@/app/utils/UStyles";
import {
  CHART_TOOLTIP_STYLE,
  isStandardCallType,
  typeColor,
  typeFill,
} from "./AnalysisOverview.logic";
import styles from "./AnalysisOverview.module.css";

type Props = {
  types: CallAnalysisOverviewType[];
};

export default function TypesChart(props: Props) {
  const T = useT();
  const { types } = props;

  const rows = types.map((entry) => ({
    name: isStandardCallType(entry.type)
      ? (T(`CALL_TYPE_${entry.type}` as TKey) as string)
      : entry.type,
    fill: typeFill(entry.type),
    color: typeColor(entry.type),
    value: entry.calls,
  }));
  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <div className={styles.card}>
      <span className={styles.cardTitle}>{T(TKey.CALL_OVERVIEW_TYPES_TITLE)}</span>

      <div className={styles.pieWrapper}>
        <div className={styles.pie}>
          <span className={styles.pieTotal}>{total}</span>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={rows}
                dataKey="value"
                nameKey="name"
                innerRadius="58%"
                outerRadius="86%"
                paddingAngle={2}
                strokeWidth={2}
              >
                {rows.map((row) => (
                  <Cell key={row.name} className={UStyles.classNames(styles.pieCell, row.fill)} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <ul className={styles.pieLegend}>
          {rows.map((row) => (
            <li key={row.name} className={styles.pieLegendItem}>
              <span className={styles.legendDot} style={{ background: row.color }} />
              <span className={styles.itemLabel} title={row.name}>
                {row.name}
              </span>
              <span className={styles.itemValue}>{Math.round((row.value / total) * 100)}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
